/**
 * Window events the card / comment embed NodeViews dispatch from their
 * FAILED-upload state, consumed by whichever composer owns the node.
 *
 * Why window events and not props: the card `image` + `fileEmbed`
 * extensions are built ONCE per editor surface in `extensions.ts`, so a
 * NodeView has no channel back to the React component that started the
 * upload. Several composers can be mounted at the same time (the sticky
 * thread composer + an inline reply; the wiki rail + page comments + an
 * anchored popover), so every listener looks the `placeholderId` up in
 * its OWN failed-uploads map and ignores keys it doesn't hold — the ids
 * are unique per session, so exactly one composer claims each action.
 *
 * Distinct names from the wiki orchestrator's `wiki:*-embed-upload`
 * events: a wiki comment composer renders on the same page as the doc
 * orchestrator, and sharing a name would make the orchestrator inspect
 * every comment-composer click.
 */

export const CARD_EMBED_RETRY_UPLOAD_EVENT = 'card-embed:retry-upload'
export const CARD_EMBED_DISMISS_UPLOAD_EVENT = 'card-embed:dismiss-upload'
/**
 * Fired from the UPLOADING state, not the failed one — the others are a
 * response to something that already went wrong, this stops something
 * that is still going. The owning composer aborts the transfer, deletes
 * the attachment if it landed in the meantime, and removes the node.
 */
export const CARD_EMBED_CANCEL_UPLOAD_EVENT = 'card-embed:cancel-upload'

/** Detail payload shared by the retry + dismiss events. */
export interface CardEmbedUploadActionDetail {
  /**
   * The node's transient placeholder id — `placeholderId` on `image`,
   * `uploadingPlaceholderId` on `fileEmbed`. Both stay on the node through
   * the failed state so the owning composer can re-target the SAME node.
   */
  placeholderId: string
}

export type CardEmbedUploadAction = 'retry' | 'dismiss' | 'cancel'

export function dispatchCardEmbedUploadAction(
  action: CardEmbedUploadAction,
  placeholderId: string,
): void {
  if (typeof window === 'undefined') return
  const name =
    action === 'retry'
      ? CARD_EMBED_RETRY_UPLOAD_EVENT
      : action === 'cancel'
        ? CARD_EMBED_CANCEL_UPLOAD_EVENT
        : CARD_EMBED_DISMISS_UPLOAD_EVENT
  window.dispatchEvent(
    new CustomEvent<CardEmbedUploadActionDetail>(name, { detail: { placeholderId } }),
  )
}
